import type { AbuseReportStatus } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatDate } from '@/features/shared/labels';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

const ALL = '__all__';
const STATUSES: AbuseReportStatus[] = ['OPEN', 'REVIEWED', 'DISMISSED', 'ACTIONED'];
const VARIANT: Record<AbuseReportStatus, 'warning' | 'info' | 'muted' | 'success'> = {
  OPEN: 'warning',
  REVIEWED: 'info',
  DISMISSED: 'muted',
  ACTIONED: 'success',
};

export function AdminReportsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('OPEN');
  const params = { status: status === ALL ? undefined : status };
  const reports = useInfiniteQuery({
    queryKey: queryKeys.admin.abuseReports(params),
    queryFn: ({ pageParam }) => api.admin.abuseReports({ ...params, cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const update = useMutation({
    mutationFn: ({ id, status }: { id: string; status: AbuseReportStatus }) =>
      api.admin.updateAbuseReport(id, { status }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'abuse-reports'] }),
    onError: (e) => toastError(e),
  });
  const rows = reports.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <AdminPage section="reports">
      <div className="mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger aria-label={t('admin.reports.status')} className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('admin.reports.all')}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`enums.abuseStatus.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {reports.isLoading && <Spinner label={t('app.loading')} />}
      {reports.error && <Alert variant="destructive">{describeError(reports.error)}</Alert>}
      {reports.data && (
        <ResponsiveTable
          caption={t('admin.sections.reports')}
          rowKey={(r) => r.id}
          rows={rows}
          empty={t('common.noResults')}
          columns={[
            {
              key: 'recipe',
              header: t('admin.reports.recipe'),
              cell: (r) => (
                <Link to={`/recipes/${r.recipeId}`} className="text-primary underline">
                  {r.recipeTitle ?? r.recipeId}
                </Link>
              ),
            },
            {
              key: 'reason',
              header: t('admin.reports.reason'),
              cell: (r) => <span className="line-clamp-3 max-w-md text-sm">{r.reason}</span>,
            },
            {
              key: 'status',
              header: t('admin.reports.status'),
              cell: (r) => (
                <Badge variant={VARIANT[r.status]}>{t(`enums.abuseStatus.${r.status}`)}</Badge>
              ),
            },
            {
              key: 'created',
              header: t('common.created'),
              cell: (r) => formatDate(r.createdAt, i18n.language, true),
            },
            {
              key: 'actions',
              header: '',
              label: t('admin.actions'),
              cell: (r) => (
                <Select
                  value={r.status}
                  onValueChange={(v) => update.mutate({ id: r.id, status: v as AbuseReportStatus })}
                >
                  <SelectTrigger aria-label={t('admin.reports.changeStatus')} className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {t(`enums.abuseStatus.${s}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ),
            },
          ]}
        />
      )}
      {reports.hasNextPage && (
        <div className="mt-3 text-center">
          <Button
            variant="outline"
            loading={reports.isFetchingNextPage}
            onClick={() => void reports.fetchNextPage()}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      )}
    </AdminPage>
  );
}
